import { isDummyProductType } from "../helper";

/** Canonical placeholder copy for admin / iframe preview (all templates). */
export const DUMMY_PREVIEW_TITLE = "Product Title";
export const DUMMY_PREVIEW_PRICE = "XXX.XX";
export const DUMMY_PREVIEW_COMPARE_AT = "XXX.XX";
export const DUMMY_PREVIEW_SAVE_BADGE = "XX% OFF";
export const DUMMY_PREVIEW_PRODUCT_ID = "weupsell-dummy-preview-product";
export const DUMMY_PREVIEW_DESCRIPTION =
  "This is placeholder copy so you can preview how a fuller product description looks in your offer. Replace it with your own benefits, materials, or sizing notes when you connect a real product.";

const DUMMY_VARIANT_TITLES = ["Small", "Medium", "Large"];

const buildDummyVariant = (productIndex = 0, variantIndex = 0) => {
  const title =
    DUMMY_VARIANT_TITLES[variantIndex] || `Option ${variantIndex + 1}`;
  const id = `dummy-variant-${productIndex}-${variantIndex}`;
  return {
    id,
    title,
    price: DUMMY_PREVIEW_PRICE,
    compare_at_price: DUMMY_PREVIEW_COMPARE_AT,
    available: true,
    inventory_quantity: 10,
    admin_graphql_api_id: `gid://shopify/ProductVariant/${id}`,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    position: variantIndex + 1,
    weight: 0,
    weight_unit: "kg",
    requires_shipping: true,
    taxable: true,
    barcode: null,
    grams: 0,
    image_id: null,
    fulfillment_service: "manual",
    inventory_management: "shopify",
    inventory_policy: "deny",
    option1: title,
    option2: null,
    option3: null,
  };
};

/** Single placeholder product for preview when no catalog product is selected. */
export const generateDummyProduct = (index = 0) => {
  const variants = DUMMY_VARIANT_TITLES.map((_, variantIndex) =>
    buildDummyVariant(index, variantIndex)
  );
  return {
    id: index === 0 ? DUMMY_PREVIEW_PRODUCT_ID : `dummy-product-${index}`,
    title: DUMMY_PREVIEW_TITLE,
    handle: "preview-product-placeholder",
    description: DUMMY_PREVIEW_DESCRIPTION,
    body_html: DUMMY_PREVIEW_DESCRIPTION,
    vendor: "Preview",
    product_type: "Preview",
    tags: ["preview-placeholder"],
    images: [],
    image: null,
    variants,
    options: [{ name: "Size", position: 1, values: [...DUMMY_VARIANT_TITLES] }],
    price: DUMMY_PREVIEW_PRICE,
    compare_at_price: DUMMY_PREVIEW_COMPARE_AT,
    available: true,
    isDummy: true,
    selling_plan_groups: [],
    collections: [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    published_at: new Date().toISOString(),
    template_suffix: null,
    status: "active",
    published_scope: "web",
    admin_graphql_api_id: `gid://shopify/Product/dummy-${index}`,
  };
};

export const generateDummyProducts = (count = 1) => {
  return Array.from({ length: count }, (_, index) => generateDummyProduct(index));
};

export const isPreviewPlaceholderProduct = (product) => {
  if (!product || typeof product !== "object") {
    return true;
  }
  if (product.isDummy === true) {
    return true;
  }
  const id = String(product.id || "");
  if (
    id.startsWith("dummy-product") ||
    id === DUMMY_PREVIEW_PRODUCT_ID
  ) {
    return true;
  }
  const title = String(product.title || "").trim().toLowerCase();
  return (
    title === "product title" ||
    title === "your product title" ||
    title === "product name"
  );
};

export const filterRealPreviewProducts = (products = []) =>
  (Array.isArray(products) ? products : []).filter(
    (product) => !isPreviewPlaceholderProduct(product)
  );

/** True when API/editor has hydrated a product with variant catalog data. */
export function isOfferProductCatalogReady(product) {
  if (!product || product.id == null) return false;
  return Array.isArray(product.variants) && product.variants.length > 0;
}

export function areOfferProductsCatalogReady(products = []) {
  if (!Array.isArray(products) || products.length === 0) return false;
  return products.every(isOfferProductCatalogReady);
}

/** Fill unfilled offer slots with dummy cards in admin preview (real products first). */
export const padProductsForPreviewSlots = (products = [], targetCount = 1) => {
  const limit = Math.max(1, Number(targetCount) || 1);
  const realProducts = filterRealPreviewProducts(products).slice(0, limit);
  if (realProducts.length >= limit) {
    return realProducts;
  }
  const dummyCount = limit - realProducts.length;
  const dummies = generateDummyProducts(dummyCount).map((product, index) => ({
    ...product,
    id: `dummy-product-slot-${realProducts.length + index}`,
  }));
  return [...realProducts, ...dummies];
};

export const shouldShowDummyProducts = (productOfferType) => {
  return isDummyProductType(productOfferType);
};

export const getDummyProductCount = (popupData, activeStep, section) => {
  const productCount =
    popupData?.generalSettings?.condition?.product?.products_to_offer_limit || 1;
  return Math.max(1, productCount);
};
